import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Eye, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    category: 'all'
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/admin/services?${params}`);
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (serviceId, newStatus) => {
    const reason = prompt('Reason for status change:');
    if (!reason) return;

    try {
      await axios.patch(`${API}/admin/services/${serviceId}/status?status=${newStatus}&reason=${encodeURIComponent(reason)}`);
      toast.success('Service status updated');
      fetchServices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <Badge className="bg-green-100 text-green-800">Active</Badge>,
      paused: <Badge className="bg-yellow-100 text-yellow-800">Paused</Badge>,
      deleted: <Badge className="bg-red-100 text-red-800">Deleted</Badge>,
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Service Listings</h1>
          <p className="text-muted-foreground">Manage all tradie service listings</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search services..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border rounded-lg bg-background"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="deleted">Deleted</option>
              </select>
              <button
                onClick={fetchServices}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Apply Filters
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Services Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Services ({services.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Service</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Tradie</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Price</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">Loading...</td>
                    </tr>
                  ) : services.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">No services found</td>
                    </tr>
                  ) : (
                    services.map((service) => (
                      <tr key={service.id} className="border-b hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="font-medium">{service.title}</div>
                          <div className="text-xs text-muted-foreground">{service.id}</div>
                        </td>
                        <td className="px-4 py-3">
                          {service.tradie?.business_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          ${service.price_base}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(service.status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {service.status === 'active' ? (
                              <button
                                onClick={() => updateStatus(service.id, 'paused')}
                                className="p-2 hover:bg-muted rounded"
                                title="Pause service"
                              >
                                <ToggleLeft className="w-4 h-4 text-yellow-600" />
                              </button>
                            ) : (
                              <button
                                onClick={() => updateStatus(service.id, 'active')}
                                className="p-2 hover:bg-muted rounded"
                                title="Activate service"
                              >
                                <ToggleRight className="w-4 h-4 text-green-600" />
                              </button>
                            )}
                            <button
                              onClick={() => updateStatus(service.id, 'deleted')}
                              className="p-2 hover:bg-muted rounded"
                              title="Delete service"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminServices;
